import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import RowActionsMenu from '../../../components/dashboard/RowActionsMenu';
import { formatDate } from '../../../utils/formatters';

// With `getRowHeight="auto"` (needed so a long trigger phrase wraps instead
// of being clipped in compact density), DataGrid stops vertically centering
// cell content — every other column's value renders pinned to the top of the
// row. Wrapping single-line cells in this restores the centering.
function CenteredCell({ children }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
      {children}
    </Box>
  );
}

export const buildLeadOriginColumns = ({ t, language, onView, onEdit, onDelete }) => [
  {
    field: 'name',
    headerName: t('leadOrigins.fields.name'),
    flex: 1,
    minWidth: 200,
    resizable: false,
    renderCell: (params) => (
      <CenteredCell>
        <Link
          component={RouterLink}
          to={`/lead-origins/${params.row.uuid}`}
          variant="body2"
          underline="hover"
        >
          {params.row.name}
        </Link>
      </CenteredCell>
    ),
  },
  {
    field: 'trigger_phrase',
    headerName: t('leadOrigins.fields.triggerPhrase'),
    width: 280,
    renderCell: (params) => (
      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
        {params.value || '—'}
      </Typography>
    ),
  },
  {
    field: 'lead_count',
    headerName: t('leadOrigins.fields.leadCount'),
    width: 140,
    type: 'number',
    align: 'left',
    headerAlign: 'left',
    renderCell: (params) => (
      <CenteredCell>
        <Chip size="small" label={params.value ?? 0} />
      </CenteredCell>
    ),
  },
  {
    field: 'created_at',
    headerName: t('leadOrigins.fields.createdAt'),
    width: 140,
    // Sorts on the raw ISO string (lexicographically equivalent to
    // chronological order); the cell below is presentation-only.
    renderCell: (params) => <CenteredCell>{formatDate(params.value, language)}</CenteredCell>,
  },
  {
    field: 'updated_at',
    headerName: t('leadOrigins.fields.updatedAt'),
    width: 140,
    renderCell: (params) => <CenteredCell>{formatDate(params.value, language)}</CenteredCell>,
  },
  {
    field: 'rowActions',
    headerName: '',
    width: 56,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    align: 'center',
    renderCell: (params) => (
      <CenteredCell>
        <RowActionsMenu
          actions={[
            {
              label: t('leadOrigins.actions.view'),
              icon: <VisibilityOutlinedIcon fontSize="small" />,
              onClick: () => onView(params.row),
            },
            {
              label: t('leadOrigins.actions.edit'),
              icon: <EditOutlinedIcon fontSize="small" />,
              onClick: () => onEdit(params.row),
            },
            {
              label: t('leadOrigins.actions.delete'),
              icon: <DeleteOutlineIcon fontSize="small" />,
              color: 'error',
              onClick: () => onDelete(params.row),
            },
          ]}
        />
      </CenteredCell>
    ),
  },
];
